/**
 * UnifiedDriftRow — source-discriminated row shape used by
 * HybridDriftManagementTab (Wave 7, TCM migration).
 *
 * Hybrid tenants have two drift sources running side-by-side:
 *   - DSC  → ConfigurationDrift (SPO + ODB only, permanent steady state)
 *   - TCM  → TcmDriftSummary    (Entra/EXO/Intune/Teams/Defender/Purview)
 *
 * The merge view needs a single row shape so the table can render both
 * without conditional branching at every cell. We keep the original row
 * on `raw` so the detail drawer can read source-specific fields without
 * re-fetching.
 *
 * Both backend endpoints expose `sortBy=severity` (see blueprint §11
 * R-NEW-5) so the first page from each source already contains the
 * highest-severity drifts. Client-side merge is a final tiebreaker —
 * not the gate.
 */

import type { ConfigurationDrift } from '@/types'
import type { TcmDriftSummary } from '@/types/tcm'
import { toDisplayState, toRiskScore100, type DriftDisplayState } from '@/lib/drift-lifecycle'

export type DriftSource = 'DSC' | 'TCM'

export interface UnifiedDriftRow {
  source: DriftSource
  /**
   * Row identity. For DSC this is `ConfigurationDrift.id` (used by the
   * drift detail drawer and approve/reject endpoints). For TCM this is
   * `TcmDriftEntity.Id` (the row PK — NOT the wire `tcmDriftId`, which is
   * the upstream Microsoft TCM driftId and is not addressable on our
   * controller routes).
   */
  id: string
  tenantId: string
  /**
   * Normalised severity. DSC uses a typed enum (`DriftSeverity`); TCM
   * stores severity as a free-form string and may be null pre-enrichment.
   * We narrow both to the lowest common contract and let the table
   * surface "—" for null.
   */
  severity: 'Critical' | 'High' | 'Medium' | 'Low' | 'Info' | null
  /**
   * DSC: `Component` (e.g. "AADConditionalAccessPolicy").
   * TCM: `ResourceType` (e.g. "microsoft.entra.conditionalaccesspolicy").
   * The string shapes differ — the table renders them side-by-side, the
   * Source badge tells the reader which namespace they're looking at.
   */
  resourceType: string
  /**
   * DSC: `ComponentName` (the actual resource name).
   * TCM: `ResourceDisplayName ?? ResourceInstanceIdentifier`.
   */
  resourceName: string | null
  /**
   * DSC: `Setting`.
   * TCM: first drifted property's PropertyPath. The full list lives on
   * `raw` for the detail drawer; the table cell shows the first to keep
   * row height stable.
   */
  propertyPath: string | null
  /**
   * DSC: `DriftStatus` string ('New' | 'Pending' | …).
   * TCM: state string ('Active' | 'Remediated' | 'Accepted' | 'Waived').
   * Kept verbatim for pipeline truth (the detail drawer's Status section
   * reads this) — the customer-facing queue renders `displayState` instead.
   */
  status: string
  /**
   * Harmonised display vocabulary (Open / Staged / Resolved / Dismissed),
   * computed from `status` via drift-lifecycle.ts. This is what the queue's
   * status pill renders — see toDisplayState() for the source-specific
   * mapping table.
   */
  displayState: DriftDisplayState
  detectedAt: string
  recommendedAction: string | null
  humanReadableDescription: string | null
  /**
   * DSC stores `riskScore` as a 0–1 double; TCM stores it as a 0–100
   * int (per Wave 6 Slice A aggregation). Kept verbatim for source-specific
   * consumers (e.g. the drawer's RiskCapsule) — the queue renders
   * `riskScore100` instead, which rescales both onto one 0–100 scale.
   */
  riskScore: number | null
  /**
   * `riskScore` rescaled onto one 0–100 integer scale via toRiskScore100().
   * Null when the source riskScore is null — never a fabricated 0.
   */
  riskScore100: number | null
  /**
   * Raw underlying row preserved so the detail drawer can read
   * source-specific fields (TCM has drifted-property arrays, business
   * impact, OPA package; DSC has oldValue/newValue, etc.) without a
   * second fetch.
   */
  raw: ConfigurationDrift | TcmDriftSummary
}

/* -------------------------------------------------------------------------- */
/* Severity normalisation                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Case-insensitive normaliser. Both DSC and TCM hand back PascalCase
 * today, but TCM is a free-form string in the DB and pre-enrichment
 * rows can carry 'Informational' (mapped to 'Info') or arbitrary noise.
 */
function normalizeSeverity(value: string | null | undefined): UnifiedDriftRow['severity'] {
  if (!value) return null
  const k = value.toLowerCase()
  if (k === 'critical') return 'Critical'
  if (k === 'high') return 'High'
  if (k === 'medium') return 'Medium'
  if (k === 'low') return 'Low'
  if (k === 'info' || k === 'informational') return 'Info'
  return null
}

/* -------------------------------------------------------------------------- */
/* Normalisers — one per source                                               */
/* -------------------------------------------------------------------------- */

export function normalizeToUnifiedRow(drift: ConfigurationDrift): UnifiedDriftRow {
  return {
    source: 'DSC',
    id: drift.id,
    tenantId: drift.tenantId,
    severity: normalizeSeverity(drift.severity),
    resourceType: drift.component,
    resourceName: drift.componentName ?? null,
    propertyPath: drift.setting ?? null,
    status: drift.status,
    displayState: toDisplayState('DSC', drift.status),
    detectedAt: drift.detectedAt,
    recommendedAction: drift.recommendedAction ?? null,
    humanReadableDescription: drift.humanReadableDescription ?? null,
    riskScore: typeof drift.riskScore === 'number' ? drift.riskScore : null,
    riskScore100: toRiskScore100('DSC', drift.riskScore),
    raw: drift,
  }
}

export function normalizeTcmToUnifiedRow(drift: TcmDriftSummary): UnifiedDriftRow {
  // TCM summary doesn't carry the per-property array (that lives on
  // TcmDriftDetail, fetched lazily by the drawer). Surface the count
  // in the propertyPath slot if the table wants to render it; otherwise
  // leave the cell silent.
  const propertyPath =
    drift.driftedPropertyCount > 0
      ? `${drift.driftedPropertyCount} ${drift.driftedPropertyCount === 1 ? 'property' : 'properties'}`
      : null
  return {
    source: 'TCM',
    id: drift.id,
    tenantId: drift.tenantId,
    severity: normalizeSeverity(drift.severity),
    resourceType: drift.resourceType,
    resourceName: drift.resourceDisplayName?.trim()
      ? drift.resourceDisplayName
      : drift.resourceInstanceIdentifier || null,
    propertyPath,
    status: drift.state,
    displayState: toDisplayState('TCM', drift.state),
    // A re-report after a baseline update carries the earlier first-seen date,
    // so a long-standing drift doesn't read (or sort) as new.
    detectedAt: drift.firstSeenDateTime ?? drift.firstReportedDateTime,
    // TCM enrichment hasn't shipped on the summary endpoint; the
    // values exist on the detail row. Leave null here — drawer fills
    // them in on open.
    recommendedAction: null,
    humanReadableDescription: null,
    riskScore: typeof drift.riskScore === 'number' ? drift.riskScore : null,
    riskScore100: toRiskScore100('TCM', drift.riskScore),
    raw: drift,
  }
}

/* -------------------------------------------------------------------------- */
/* Sort helper                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Severity ranking — Critical highest, null lowest. Used as the primary
 * key when merging DSC + TCM result sets. The backend has already done
 * a per-source severity-DESC sort (via `sortBy=severity`); this is the
 * cross-source tiebreaker.
 */
const SEVERITY_RANK: Record<NonNullable<UnifiedDriftRow['severity']>, number> = {
  Critical: 5,
  High: 4,
  Medium: 3,
  Low: 2,
  Info: 1,
}

export function compareUnifiedRows(a: UnifiedDriftRow, b: UnifiedDriftRow): number {
  const rankA = a.severity ? SEVERITY_RANK[a.severity] : 0
  const rankB = b.severity ? SEVERITY_RANK[b.severity] : 0
  if (rankA !== rankB) return rankB - rankA // higher severity first
  // Newest first as the final tiebreaker — same convention DSC and TCM
  // use in their recency sort.
  const tA = new Date(a.detectedAt).getTime()
  const tB = new Date(b.detectedAt).getTime()
  if (Number.isNaN(tA) || Number.isNaN(tB)) return 0
  return tB - tA
}
